import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useCopy } from '../../i18n';
import { SHORTCUT_MAP } from '../../hooks/useKeyboardShortcuts';
import { Keyboard } from 'lucide-react';

interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const KeyBadge = ({ children }: { children: string }) => (
  <kbd className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 text-[11px] font-mono font-medium bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] border border-[var(--color-border)] rounded-[var(--radius-sm)] shadow-[0_1px_0_var(--color-border)]">
    {children}
  </kbd>
);

const formatKeys = (shortcut: string) => {
  const parts = shortcut.split('+');
  return (
    <span className="flex items-center gap-1">
      {parts.map((part, i) => (
        <span key={i} className="flex items-center gap-0.5">
          {i > 0 && <span className="text-[var(--color-text-muted)] text-[10px]">+</span>}
          <KeyBadge>{part}</KeyBadge>
        </span>
      ))}
    </span>
  );
};

export default function KeyboardShortcutsModal({ isOpen, onClose }: KeyboardShortcutsModalProps) {
  const t = useCopy().shell;
  const categories = [...new Set(SHORTCUT_MAP.map(s => s.category))];

  // A tecla é um <kbd>, não texto: a frase quebra no slot `{{key}}` porque a
  // posição dele muda com o idioma.
  const [hintBefore, hintAfter = ''] = t.shortcuts.hint.split('{{key}}');

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard size={20} />
            {t.header.shortcutsTitle}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {categories.map(category => (
            <div key={category}>
              <h3 className="text-[12px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-2">
                {t.shortcuts.groups[category]}
              </h3>
              <div className="space-y-1.5">
                {SHORTCUT_MAP.filter(s => s.category === category).map(shortcut => (
                  <div
                    key={shortcut.key}
                    className="flex items-center justify-between py-1.5 px-2 rounded-[var(--radius-sm)] hover:bg-[var(--color-bg-secondary)]"
                  >
                    <span className="text-[13px] text-[var(--color-text)]">{t.shortcuts.list[shortcut.labelKey]}</span>
                    {formatKeys(shortcut.key)}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-3 border-t border-[var(--color-border)] text-center">
          <p className="text-[11px] text-[var(--color-text-muted)]">
            {hintBefore}<KeyBadge>?</KeyBadge>{hintAfter}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
